/**
 * Web Storage that is allowed to not exist.
 *
 * Storage does not merely come back empty when a browser refuses it — touching
 * it THROWS. Safari raises "SecurityError: The operation is insecure" on any
 * access when the visitor blocks cookies, which blocks storage along with them
 * (Sentry SECRETSOVERFLOW_FRONTEND-C, desktop Safari 27). Some browsers throw on
 * the `window.sessionStorage` property itself rather than on its methods, so the
 * property access sits inside the `try` too.
 *
 * Reads distinguish "no value" from "no storage" because some callers must act
 * differently: a guard that cannot record anything cannot guard anything.
 */

type StorageKind = 'session' | 'local';

const pick = (kind: StorageKind): Storage =>
  kind === 'session' ? window.sessionStorage : window.localStorage;

/** The stored value, `null` when there is none, or `undefined` when storage is unavailable. */
export function readStorage(kind: StorageKind, key: string): string | null | undefined {
  try {
    return pick(kind).getItem(key);
  } catch {
    return undefined;
  }
}

/** Whether the value was actually stored. */
export function writeStorage(kind: StorageKind, key: string, value: string): boolean {
  try {
    pick(kind).setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function removeStorage(kind: StorageKind, key: string): void {
  try {
    pick(kind).removeItem(key);
  } catch {
    // Nothing can be stored here, so there is nothing to remove.
  }
}
